/** @module Auth0PageBase */
/* eslint-disable import/extensions */
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';
import { resolveSsasBaseUrl } from '../../utils/ssas-urls.mjs';
import Auth0SentryReporter from '../../utils/auth0-sentry-reporter.mjs';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import PageTemplate from '../../../public/pageTemplate.html.mst';
import SpinnerHtml from '../../../public/pleaseWait.html.mst';
import Auth0DynamicScriptLoader from '../../utils/auth0-dynamic-script-loader.mjs';
import Auth0WhiteLabelAssetHelper from '../utils/auth0-white-label-helper.mst.mjs';
import Auth0CookieHelper from '../../utils/auth0-cookie-helper.mjs';
import Auth0Constants from '../../utils/auth0-constants.mjs';
import Auth0UserSessionHelper from '../../utils/auth0-session-helper.mjs';
import logger from '../../utils/logging/auth0-logger.mjs';
import Auth0FeatureFlagHelper from '../../utils/auth0-feature-flag-helper.mjs';
import Auth0ManagementApiBrowser from '../../utils/auth0-management-api-browser.mjs';
import Auth0BetaOptInHelper from '../../utils/beta-opt-in/auth0-beta-opt-in-helper.mjs';
import Auth0YofiTelemetryHelper from '../../utils/auth0-yofi-telemetry-helper.mjs';
import Auth0ClientEx from '../utils/auth0-client-ex.mst.mjs';
import AuthAnalytics from '../../utils/auth-analytics.mjs';

/**
 * @abstract
 */
class Auth0PageBase {
  constructor(options) {
    console.log(`New Auth0PageBase => ${JSON.stringify(options)}`);

    this._name = options?.moduleName || options.name;
    this._initLogger();
    this._whiteLabelOptions = options?.whiteLabelOptions || {};
    this._pageScripts = options.pageScripts;
    this._pageStyles = options.pageStyles;
    this._animationStyle = 'fade' || this.ANIMATION_STYLE_NONE;

    this._setInternalClientId(options.clientId || options.clientID || Auth0UserSessionHelper.userAccessTokenClientId);

    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options?.tenantOrDomain || options.tenant);
    this._domain = resolved.domain;
    this._tenant = resolved.tenant;
    this._spinnerHtml = SpinnerHtml;

    // Set tenant class on logger for auto-injection into all SumoLogic messages
    logger.setTenantClass(this._tenant?.tenantClass ?? null);

    // Initialize Segment Analytics (safe to call multiple times)
    // Pass tenantClass for auto-injection into all track payloads
    AuthAnalytics.initialize([{
      type: 'segment',
      writeKey: 'N2eGR8jkGRDR51XZrOJyDr4sfQFbKgIb',
      tenantClass: this._tenant?.tenantClass ?? null,
    }]);

    this._qs = Auth0UtilsBrowser.parseQueryString();

    // Note: the template base ALWAYS loads 'https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/css/bootstrap.min.css',
    this._defaultStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-default-styles.css?cb=71c5d67`,
    ];

    // Note: the template page always loads { url: 'https://cdn.jsdelivr.net/npm/bootstrap@5.2.3/dist/js/bootstrap.bundle.min.js', attributes: { crossorigin: 'anonymous' } },
    this._defaultScripts = [Auth0YofiTelemetryHelper.getInitializerScript()];

    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: this._tenant.id,
      ...this._whiteLabelOptions,
    });
    this._scriptLoader = new Auth0DynamicScriptLoader(this._tenant.id);
    this._ffClient = null;

    this.__appLoginUrl = options.appLoginUrl || document.location.origin;

    this._csrf = options.internalOptions?._csrf;
  }

  _setInternalClientId = (clientId) => {
    this._clientId = clientId;

    if (this._clientId) {
      this._client = new Auth0ClientEx(clientId);
    }
  };

  get _appLoginUrl() {
    return this.__appLoginUrl;
  }

  get ANIMATION_STYLE_NONE() { return 'none'; }

  get ANIMATION_STYLE_FADE() { return 'fade'; }

  get ANIMATION_STYLE_SLIDE() { return 'slide'; }

  get body() {
    return this._body;
  }

  set body(value) {
    this._body = value;
    this._contentContainer.innerHTML = this._body;
  }

  get toolbarVisible() {
    return !!this._toolbarVisible;
  }

  set toolbarVisible(value) {
    this._toolbarVisible = value;
    this._showElement('toolbar-container', value);
  }

  get languageSelectorVisible() {
    return !!this._languageSelectorVisible;
  }

  set languageSelectorVisible(value) {
    this._languageSelectorVisible = value;
    this._showElement('language-selector-container', value, 'inline-block');
    this._hideShowActionBarElementSeparator();
  }

  get actionLinkVisible() {
    return !!this._actionLinkVisible;
  }

  set actionLinkVisible(value) {
    this._actionLinkVisible = value;
    this._showElement('toolbar-action-cancel', value, 'inline');
    this._hideShowActionBarElementSeparator();
  }

  get actionLinkText() {
    return this._actionLinkText;
  }

  set actionLinkText(value) {
    this._actionLinkText = value;
    this._actionLinkContainer.innerText = value;
  }

  get actionLinkTextHandler() {
    return this._actionLinkTextHandler;
  }

  set actionLinkTextHandler(value) {
    this._actionLinkTextHandler = value;
    this._actionLinkContainer.onclick = value;
  }

  get actionLinkHtml() {
    return this._actionLinkHtml;
  }

  set actionLinkHtml(value) {
    this._actionLinkHtml = value;
    const customActionEle = this._actionLinkContainer;
    customActionEle.classList.remove(...customActionEle.classList);
    customActionEle.classList.add('toolbar-action-item');
    customActionEle.innerHTML = value;
  }

  get _contentContainer() {
    return document.getElementById('content-container');
  }

  get _languageSelectorContainer() {
    return document.getElementById('toolbar-action');
  }

  get _actionLinkContainer() {
    return document.getElementById('toolbar-action-cancel');
  }

  get _spinnerContainer() {
    return document.getElementById('content-container');
  }

  set _spinnerContainer(value) {
    document.getElementById('content-container').innerHTML = value;
  }

  get _pageWasLoadedViaRefresh() {
    const [navigationEntry] = performance.getEntriesByType('navigation');
    // console.log('navigationEntry', JSON.stringify(navigationEntry));
    return navigationEntry?.type === 'reload';
  }

  _hideShowActionBarElementSeparator = () => {
    // only show if both the lang selector and the options toolbar action are both visible.
    if (this.languageSelectorVisible && this.actionLinkVisible) {
      this._actionLinkContainer.classList.add('toolbar-separator');
    } else {
      this._actionLinkContainer.classList.remove('toolbar-separator');
    }
  };

  _showElement = (ele, show = true, displayType = 'block') => {
    if (typeof ele === 'string') {
      ele = document.getElementById(ele);
    }
    ele.style.display = show ? displayType : 'none';
  };

  _hideElement = (ele) => {
    this._showElement(ele, false);
  };

  _enableButton = (buttonOrButtonId, buttonTextKey = null, enable = true) => {
    const buttonEle = typeof buttonOrButtonId === 'string'
      ? document.getElementById(buttonOrButtonId)
      : buttonOrButtonId;

    buttonEle.disabled = !enable;
    if (enable) {
      buttonEle.classList.remove('disabled-button');
    } else {
      buttonEle.classList.add('disabled-button');
    }

    if (buttonTextKey) {
      Auth0Localizer.setElementLocalizedText({ id: buttonEle.id, resourceIdOverride: buttonTextKey });
    }
  };

  _disableButton = (buttonOrButtonId, buttonTextKey = null) => {
    this._enableButton(buttonOrButtonId, buttonTextKey, false);
  };

  _initLogger = () => {
    logger.initialize({
      sumoEndpoint: 'https://endpoint2.collection.us2.sumologic.com/receiver/v1/http/ZaVnC4dhaV1uAI96duDvnKsBH-5zBCQS4Z9yRC6qyLXrEdfezmbhON_4so37Qor_6qM1eto00rRWjhlaqRccMgcVRvrLAKRfcyNBdeziv7B_grZ3H8sjew==',
      sourceName: this._name,
    });
  };

  _checkForAndDisplayErrorMessage = () => {
    const msg = this.errorMessage || this._extractErrorMessage();
    console.log(`Extracted error message: ${msg}`);
    if (msg) {
      this._displayError(decodeURIComponent(msg));
    }
  };

  _setErrorMessageAndReload = (errMsg, redirectOverride = null) => {
    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(Auth0Constants.ERROR_MESSAGE, errMsg, 30);
    if (redirectOverride) {
      console.log(`Redirecting to override URL: ${redirectOverride}`);
      document.location.href = redirectOverride;
      return;
    }

    document.location.reload();
  };

  _checkForAndDisplayInfoMessage = () => {
    const msg = this.infoMessage || this._extractInfoMessage();
    if (msg) {
      this._displayInfo(decodeURIComponent(msg));
    }
  };

  _writeMessageForNextLoginPageRender = (msg, replacements, ttlSec = 30) => {
    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.INFO_MESSAGE,
      value: Auth0Localizer.getLocalizedText(msg, replacements),
      expirySec: ttlSec,
    });
  };

  _hideError = (hideErrorDelayMs = 0) => {
    Auth0UtilsBrowser.hideError(hideErrorDelayMs);
  };

  _displayError = (err, errReplacementArgs = null) => {
    console.log(`Displaying error: ${err}`);
    const localizationKey = Auth0Localizer.resourceKeyFromMessage(err);
    if (localizationKey) {
      Auth0Localizer.displayLocalizedError(localizationKey, errReplacementArgs);
      return;
    }
    // give up... just display what we have
    Auth0UtilsBrowser.displayError(err);
  };

  _extractErrorMessage = (deleteAfterRead = true) => {
    let errorMsg;
    const errorCookie = Auth0CookieHelper.getCookieValue(Auth0Constants.ERROR_MESSAGE);
    if (errorCookie) {
      console.log(`Found error cookie: ${errorCookie}`);
      errorMsg = Auth0Localizer.isValidLocalizationKey(errorCookie)
        ? Auth0Localizer.getLocalizedText(errorCookie)
        : errorCookie;

      if (deleteAfterRead) {
        Auth0CookieHelper.deleteCookie(Auth0Constants.ERROR_MESSAGE);
      }
    }

    if (this.config.extraParams.error_msg) {
      const msg = Auth0Localizer.isValidLocalizationKey(this.config.extraParams.error_msg)
        ? Auth0Localizer.getLocalizedText(this.config.extraParams.error_msg)
        : this.config.extraParams.error_msg;
      errorMsg = errorMsg ? `${errorMsg}\n\n${msg}` : msg;
    }

    return errorMsg;
  };

  _extractInfoMessage = (deleteAfterRead = true) => {
    let infoMsg;
    const infoCookie = Auth0CookieHelper.getCookieValue(Auth0Constants.INFO_MESSAGE);
    if (infoCookie) {
      infoMsg = Auth0Localizer.isValidLocalizationKey(infoCookie)
        ? Auth0Localizer.getLocalizedText(infoCookie)
        : infoCookie;

      if (deleteAfterRead) {
        Auth0CookieHelper.deleteCookie(Auth0Constants.INFO_MESSAGE);
      }
    }

    if (this.config.extraParams.info_msg) {
      const msg = Auth0Localizer.isValidLocalizationKey(this.config.extraParams.info_msg)
        ? Auth0Localizer.getLocalizedText(this.config.extraParams.info_msg)
        : this.config.extraParams.info_msg;
      infoMsg = infoMsg ? `${infoMsg}\n\n${msg}` : msg;
    }

    return infoMsg;
  };

  _handledUnexpectedException = (e) => {
    logger.error(`Unexpected error in ${this._name}`, e);
    logger.log(Auth0UtilsBrowser.errorToString(e));
    if (this.sentry) {
      this.sentry.captureException(e);
    }
  };

  _displayInfo = (info, infoReplacementArgs = null) => {
    const localizationKey = Auth0Localizer.resourceKeyFromMessage(info);
    if (localizationKey) {
      Auth0Localizer.displayLocalizedInfo(localizationKey, infoReplacementArgs);
    } else {
      Auth0UtilsBrowser.displayInfo(info);
    }
  };

  _hideInfo = () => {
    Auth0UtilsBrowser.hideInfo();
  };

  _showPage = () => {
    document.getElementById('root-table').style.display = '';
  };

  _cookiesEnabledCheck = () => {
    console.log('cookie check');
    if (!Auth0CookieHelper.browserCookiesEnabled) {
      console.log('cookie disabled');
      this._displayError('cookies-required', { appName: this._appName });
    }
  };

  _initFfProvider = async (auth0ClientId) => {
    if (this._ffClient) {
      return this._ffClient;
    }
    this._ffClient = new Auth0FeatureFlagHelper({
      auth0Tenant: this.auth0Tenant,
      clientSideId: '5ac681befa82d72c70a637dc',
    });
    await this._ffClient.initialize({
      kind: 'user',
      anonymous: true,
      'auth0-client-id': auth0ClientId,
    });
    return this._ffClient;
  };

  _showEmbeddedStatusWidget = async (statusWidgetUrl) => {
    try {
      if (statusWidgetUrl && typeof statusWidgetUrl === 'string') {
        const scriptEl = document.createElement('script');
        scriptEl.src = statusWidgetUrl;
        document.body.appendChild(scriptEl);
      }
    } catch (error) {
      // non-critical code block, do not fail login page load if this fails
      logger.warn('WARNING: ShipStatus embedded status widget did not load.', error);
    }
  };

  async _applyWhiteLabel() {
    console.log(`_applyWhiteLabel for ${this._name} => ${JSON.stringify(this._whiteLabelOptions)}`);
    await this._whiteLabelHelper.loadCustomStyles();

    document.getElementById('ss-logo').src = this._whiteLabelHelper.brandLogoUrl;
    this._whiteLabelHelper.setFavIcons();
  }

  async _localize() {
    await Auth0Localizer.enableMarkdownConversion();

    Auth0Localizer.initLocalizationContextMenu({
      contextMenuLinkIds: ['language-code', 'language-icon'],
      flagIconsUrl: Auth0Cdn.AUTH0_IMAGES_URL,
    });
  }

  _applyDefaultUiComponentVisibility() {
    console.log('Auth0PageBase: setting toolbar defaults');
    // applies default visibility if concrete page does not set it
    if (this._toolbarVisible === undefined) {
      console.log('Auth0PageBase: toolbarVisible is not set, setting default');
      this.toolbarVisible = true;
    }
    if (this._actionLinkVisible === undefined) {
      console.log('Auth0PageBase: actionLinkVisible is not set, setting default');
      this.actionLinkVisible = true;
    }
    if (this._languageSelectorVisible === undefined) {
      console.log('Auth0PageBase: languageSelectorVisible is not set, setting default');
      this.languageSelectorVisible = !this._client?.isMobileApp || false;
    }
  }

  /**
   * @function
   * @async
   * @description Directs the page to start its process. Derived class is expected to override this...
   */
  async _startUi() {
    this.sentry = await Auth0SentryReporter.reporterFactory(this._tenant.id, 'https://6e0ea9c8e7fb4591b45a8c455a5e7618@o106853.ingest.sentry.io/5433855');
    await this._applyWhiteLabel();
    await this._localize();

    // allow concrete classes to set component visibility in their own _applyWhiteLabel methods
    this._applyDefaultUiComponentVisibility();

    if (this._notificationMessage) {
      this._displayInfo(this._notificationMessage, this._notificationMessageReplacementArgs);
    }
  }

  render(forceRerender = false) {
    return new Promise((resolve) => {
      console.log('Auth0PageBase render start =>', this._name);
      this._pageScripts = this._pageScripts ?? [];
      this._pageStyles = this._pageStyles ?? [];
      const onRendered = async () => {
        console.log('Auth0PageBase render complete =>', this._name);
        console.log('Auth0PageBase load styles...');
        const pageStyles = [
          ...this._defaultStyles,
          ...this._pageStyles,
        ];
        await this._scriptLoader.loadStyles(pageStyles);
        const pageScripts = [
          ...this._defaultScripts,
          ...this._pageScripts,
        ];
        console.log('Auth0PageBase load scripts...');
        await this._scriptLoader.loadScripts(pageScripts, true, false);
        console.log('Auth0PageBase: add unload handlers...');
        window.removeEventListener('beforeunload', this._onUnloadWrapper);
        window.addEventListener('beforeunload', this._onUnloadWrapper);
        this._initAnimations();
        await this._onLoad();
        resolve();
      };

      if (!forceRerender && document.getElementById('content-container')) {
        onRendered();
      } else {
        Auth0UtilsBrowser.replaceCurrentDocument(PageTemplate, onRendered);
      }
    });
  }

  _initAnimations = () => {
    if (this._animationStyle === this.ANIMATION_STYLE_FADE) {
      console.log('Auth0PageBase init for fade animation...');
      this._contentContainer.classList.add('content-container-fade');
      this._contentContainer.classList.add('fade-out');
    }
  };

  async setBody(newContent, animationStyle = null) {
    animationStyle = animationStyle || this._animationStyle;
    if (animationStyle === this.ANIMATION_STYLE_NONE) {
      this.body = newContent;
      await this._startUi();
      return;
    }
    if (animationStyle === this.ANIMATION_STYLE_FADE) {
      console.log('FADE OUT REQUEST BY', this._name);
      await this._fadeOutReplace(newContent); // _startUi called when fade out is complete, but before fade in
      return;
    }
    if (animationStyle === this.ANIMATION_STYLE_SLIDE) {
      // not currently functional
      await this._slideOutReplace(newContent); // _startUi called when fade out is complete, but before fade in
    }
  }

  _fadeOutReplace = async (newContent) => {
    await this._fadeOutDiv(this._contentContainer);
    await this._fadeInReplaceDiv(this._contentContainer, newContent, async () => {
      await this._startUi();
    });
  };

  _fadeOutDiv = (contentDiv) => {
    // fade out div contentDiv, replace content with newContent and fade in
    return new Promise((resolve) => {
      if (!contentDiv) {
        resolve();
        return;
      }

      const contentDivIsEmpty = () => {
        return !contentDiv.innerHTML.trim();
      };

      const fadeOut = () => {
        // Force a reflow, so the browser registers the display change
        // eslint-disable-next-line no-unused-expressions
        contentDiv.offsetHeight; // This line ensures the transition works by forcing a reflow
        contentDiv.classList.remove('fade-in');
        contentDiv.classList.add('fade-out');
      };

      const transitionEndHandler = () => {
        contentDiv.removeEventListener('transitionend', transitionEndHandler);
        resolve();
      };

      if (contentDivIsEmpty()) {
        transitionEndHandler();
      } else {
        // full fade out and then in
        contentDiv.addEventListener('transitionend', transitionEndHandler, { once: true });
        fadeOut();
      }
    });
  };

  _fadeInReplaceDiv = (contentDiv, newContent, beforeFadeInUiInitCallback = null) => {
    return new Promise(async (resolve) => {
      if (!contentDiv) {
        resolve();
        return;
      }

      const fadeIn = async () => {
        console.log('fade in start');
        contentDiv.innerHTML = newContent;
        if (beforeFadeInUiInitCallback) {
          await beforeFadeInUiInitCallback();
          console.log('fade in after init');
        }

        // Force a reflow, so the browser registers the display change
        // eslint-disable-next-line no-unused-expressions
        contentDiv.offsetHeight; // This line ensures the transition works by forcing a reflow
        contentDiv.classList.remove('fade-out');
        contentDiv.classList.add('fade-in');
      };

      const transitionEndHandler = () => {
        console.log('fade in done');
        contentDiv.removeEventListener('transitionend', transitionEndHandler);
        resolve();
      };

      contentDiv.addEventListener('transitionend', transitionEndHandler, { once: true });
      await fadeIn();
    });
  };

  _fadeOutAlterThenFadeBackIn = (alterCallback, divIdOrDiv = 'content-container') => {
    return new Promise(async (resolve) => {
      const contentDiv = typeof divIdOrDiv === 'string'
        ? document.getElementById(divIdOrDiv)
        : divIdOrDiv;
      if (!contentDiv) {
        resolve();
        return;
      }
      const fadeIn = () => {
        console.log('fade in start');
        return new Promise((resolveFadeIn) => {
          contentDiv.addEventListener('transitionend', () => { resolveFadeIn(); }, { once: true });
          // Force a reflow, so the browser registers the display change
          // eslint-disable-next-line no-unused-expressions
          contentDiv.offsetHeight; // This line ensures the transition works by forcing a reflow
          contentDiv.classList.remove('fade-in');
          contentDiv.classList.remove('fade-out');
          contentDiv.classList.add('fade-in');
        });
      };

      const fadeOut = () => {
        console.log('fade out start');
        return new Promise((resolveFadeOut) => {
          contentDiv.addEventListener('transitionend', () => { resolveFadeOut(); }, { once: true });
          // Force a reflow, so the browser registers the display change
          // eslint-disable-next-line no-unused-expressions
          contentDiv.offsetHeight; // This line ensures the transition works by forcing a reflow
          contentDiv.classList.remove('fade-out');
          contentDiv.classList.remove('fade-in');
          contentDiv.classList.add('fade-out');
        });
      };

      console.log('BEFORE-FADE-OUT');
      await fadeOut();
      console.log('ALTER');
      alterCallback();
      console.log('BEFORE-FADE-IN');
      await fadeIn();
    });
  };

  // eslint-disable-next-line no-unused-vars
  _slideOutReplace = (newContent) => {
    // TODO
  };

  async _onLoad() {
    // to be implemented by derived class
  }

  _onUnloadWrapper = async () => {
    await this._onUnload();
  };

  async _onUnload() {
    // to be implemented by derived class
  }

  // eslint-disable-next-line no-unused-vars
  _preLogoutActions(logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) {
    Auth0UserSessionHelper.clearAuthCookies();
  }

  _logout(logoutType = Auth0Constants.LOGOUT_TYPE_REDIRECT) {
    this._preLogoutActions(logoutType);

    Auth0UtilsBrowser.setOption({
      cookieName: Auth0Constants.AUTH0_FORCE_LOGOUT,
      value: { logoutType },
      expirySec: 600,
      stringify: true,
    });
    document.location.href = this._appLoginUrl;
  }

  showSpinner = async (pleaseWaitTextKey = 'please-wait-generic', pleaseWaitReplacementVars = null) => {
    console.log('show spinner');
    const spinnerDiv = this._spinnerContainer;
    if (!spinnerDiv) {
      console.log('No spinner div!');
      return;
    }

    const contentDiv = this._contentContainer;
    // quick fade out of anything visible now
    if (contentDiv && contentDiv.innerHTML.trim() && !contentDiv.classList?.contains('fade-out')) {
      this._contentContainerRestoreCache = this._contentContainer.innerHTML;
      await this._fadeOutDiv(this._contentContainer);
    }

    const spinnerUiInit = async () => {
      document.getElementById('wait-spinner-icon').src = this._whiteLabelHelper.brandWaitingSpinnerIconUrl;
      Auth0Localizer.setElementLocalizedText({
        id: 'please-wait',
        resourceIdOverride: pleaseWaitTextKey,
      }, pleaseWaitReplacementVars);
      await Promise.resolve();
    };

    await this._fadeInReplaceDiv(this._spinnerContainer, SpinnerHtml, spinnerUiInit);

    this.toolbarVisible = false;
    console.log('show spinner end');
  };

  hideSpinner = async (spinnerDivOverride = null) => {
    console.log('hide spinner');
    const spinnerDiv = spinnerDivOverride || this._spinnerContainer;
    if (!spinnerDiv) return;

    // quick fade out
    await this._fadeOutDiv(spinnerDiv);

    if (!this._contentContainerRestoreCache) return;
    console.log('restore contents');
    // restore previous contents
    await this._fadeInReplaceDiv(this._contentContainer, this._contentContainerRestoreCache);
  };

  get _inlineSpinner() {
    return document.getElementById('inline-spinner');
  }

  _isSpinnerVisible = () => {
    return !!document.getElementById('wait-spinner-icon');
  };

  get _auth0ManagementApi() {
    if (!this._a0Api) {
      this._a0Api = new Auth0ManagementApiBrowser({
        tenantOrDomain: this._tenant.id,
        clientId: this._clientId,
        authServiceBaseUrl: resolveSsasBaseUrl('auth-stage', 'shipstation.com,sslocal.com', window.location.hostname),
      });
    }
    return this._a0Api;
  }

  get _optInHelper() {
    if (!this.__optInHelper) {
      this.__optInHelper = new Auth0BetaOptInHelper({
        tenantOrDomain: this.auth0Tenant,
        ldClientSideId: '5e90963427fe4107f79183bb',
      });
    }
    return this.__optInHelper;
  }
}

export { Auth0PageBase };
